import { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '~components/ui/button'
import TextButton from '~components/ui/button/text-button'
import Icon from '~components/ui/icon'
import useMyProfile from '~repositories/hooks/use-my-profile'
import getRootPath from '~utils/app/get-root-path'

import { useNavigate } from '@tanstack/react-router'

import { AdditionalContactInfoBottomSheet } from './components/additional-contact-info.bottomsheet'
import { resetConsultationData } from './store/consultation-data'
import Mixpanel from '~utils/mixpanel'

interface ConsultationSubmittedProps {
  submittedConsultationId: number | string
}
const Submitted = ({ submittedConsultationId }: ConsultationSubmittedProps) => {
  const { data: myProfile } = useMyProfile()
  const [openSheet, setOpenSheet] = useState(false)
  const navigate = useNavigate()

  const handleGoToConsultationClick = useCallback(() => {
    navigate({
      to: `${getRootPath()}/consultation/${submittedConsultationId}`,
      search: {
        referrer: 'ask-a-vet',
      },
      resetScroll: true,
    })
  }, [navigate, submittedConsultationId])

  const needAdditionalContactInfo = useMemo(() => !myProfile?.phone_number, [myProfile])

  useEffect(() => {
    resetConsultationData()
    Mixpanel.logEvent('askavet: completed', {
      consultation_id: submittedConsultationId,
      is_user_scenario: true
    })
  }, [])

  return (
    <div className="flex flex-col items-center min-h-full animate-fade-in-400ms">
      <div className="flex flex-col items-center flex-gap-y-4 p-4 pt-6 h-[444px] w-full justify-center">
        <Icon name="check_circle_solid" className="size-16 fill-green-500 dark:fill-gray-700" />
        <h1 className="text-gray-900 text-headline-3 dark:text-white">We’re on it!</h1>
        <p className="whitespace-pre-line text-center text-gray-700 text-body-2 dark:text-gray-500">
          {`Your questions will be reviewed shortly.\nYou will get notified as soon as the answers are ready.`}
        </p>
      </div>
      <Divider />
      <div className="flex items-center justify-center w-full p-5">
        <div className="w-full p-5 rounded-[16px] bg-extended-alabaster dark:bg-inherit dark:border-gray-800 dark:border">
          <p className="text-gray-500 text-body-2 dark:text-gray-700">
            {`Dr.Tail’s vet consultation is based on the information you provide. The answers are only advice and not medical judgment. If you’re clearly in an emergency situation, immediately visit the hospital.`}
          </p>
        </div>
      </div>
      <section className="sticky bottom-0 w-full flex-1 flex flex-col flex-gap-y-2 p-5 bg-background justify-end">
        {needAdditionalContactInfo ? (
          <>
            <Button className="w-full" priority="primary" onClick={() => setOpenSheet(true)}>
              Text me when ready
            </Button>
            <TextButton
              className="!bg-transparent"
              priority="tertiary"
              onClick={handleGoToConsultationClick}
            >
              Go to my consultation
            </TextButton>
            <AdditionalContactInfoBottomSheet
              open={openSheet}
              onClose={() => setOpenSheet(false)}
            ></AdditionalContactInfoBottomSheet>
          </>
        ) : (
          <Button className="w-full" priority="primary" onClick={handleGoToConsultationClick}>
            Go to my consultation
          </Button>
        )}
      </section>
    </div>
  )
}

const Divider = () => {
  return <div className="h-2 w-full bg-gray-300 dark:bg-inherit" />
}

export default Submitted
